import React, { useMemo } from 'react';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { User } from '../../../../state/user';
import { Delete as DeleteIcon } from '@material-ui/icons';
import { FieldArray, Form, Formik, getIn } from 'formik';
import UserComboBox from '../../../components/UserComboBox';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { ISubtaskType } from '../../../../models/subtaskType.model';
import { AppState } from '../../../../state/configureStore';
import {
  createSubtasksInTaskCreate,
  TaskCreateState,
} from '../../../../state/taskCreate';
import { AuthState } from '../../../../state/auth';
import {
  Close as CloseIcon,
  SkipNext as NextIcon,
  SkipPrevious as PreviousIcon,
} from '@material-ui/icons';

interface SubtaskListDetailDialogProps {
  nextClick: () => void;
  backClick: () => void;
  closeClick: () => void;
  subtaskTypeList: ISubtaskType[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      padding: 0,
      background:
        'radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 30%), linear-gradient(135deg, #072577 0%, #1d4ed8 55%, #60a5fa 100%)',
    },
    titleContent: {
      padding: theme.spacing(2.25, 3, 1.75),
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    titleText: {
      flex: 1,
      minWidth: 0,
    },
    titleHeading: {
      color: '#f8fafc',
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    titleSubtitle: {
      color: 'rgba(226, 232, 240, 0.84)',
      lineHeight: 1.65,
      maxWidth: 720,
    },
    titleBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.9, 1.5),
      borderRadius: 999,
      border: '1px solid rgba(255,255,255,0.14)',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 100%)',
      color: '#ffffff',
      fontSize: '0.82rem',
      fontWeight: 700,
      backdropFilter: 'blur(8px)',
    },
    titleActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    closeButton: {
      color: '#ffffff',
      backgroundColor: 'rgba(255,255,255,0.12)',
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.2)',
      },
    },
    content: {
      padding: theme.spacing(2.5, 3, 2),
      background:
        'linear-gradient(180deg, rgba(248,250,252,0.94) 0%, rgba(255,255,255,1) 100%)',
    },
    tableMeta: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
    },
    metaTitle: {
      fontWeight: 700,
      color: '#0f172a',
    },
    metaSubtitle: {
      color: '#64748b',
      lineHeight: 1.6,
    },
    countBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: theme.spacing(0.85, 1.5),
      borderRadius: 999,
      fontSize: '0.82rem',
      fontWeight: 700,
      color: '#1d4ed8',
      background: 'rgba(219, 234, 254, 0.9)',
      border: '1px solid rgba(96, 165, 250, 0.35)',
    },
    tableSurface: {
      borderRadius: 14,
      overflow: 'hidden',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.96) 100%)',
      border: '1px solid rgba(148, 163, 184, 0.18)',
      boxShadow: '0 20px 40px rgba(15, 23, 42, 0.08)',
    },
    tableScrollArea: {
      overflowX: 'auto',
    },
    tableHeadRow: {
      background: 'rgba(241, 245, 249, 0.9)',
    },
    tableHeadCell: {
      borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
      color: '#ffffff',
      fontWeight: 800,
      fontSize: '0.78rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      whiteSpace: 'nowrap',
    },
    resultRow: {
      '&:not(:last-child) td': {
        borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
      },
      '&:hover': {
        background: 'rgba(248, 250, 252, 0.85)',
      },
    },
    subjectCell: {
      width: '30%',
      minWidth: 240,
      verticalAlign: 'top',
    },
    typeCell: {
      width: '12%',
      minWidth: 120,
      verticalAlign: 'top',
    },
    assigneeCell: {
      width: '35%',
      minWidth: 280,
      verticalAlign: 'top',
    },
    dueDateCell: {
      width: '18%',
      minWidth: 190,
      verticalAlign: 'top',
    },
    actionCell: {
      width: '5%',
      minWidth: 72,
      verticalAlign: 'top',
    },
    typeBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(1.1),
      minHeight: 48,
      minWidth: 130,
      padding: theme.spacing(0.9, 1.5),
      borderRadius: 16,
      color: '#0f172a',
      background:
        'linear-gradient(135deg, rgba(219,234,254,0.95) 0%, rgba(239,246,255,0.98) 100%)',
      border: '1px solid rgba(96, 165, 250, 0.3)',
      boxShadow: '0 10px 22px rgba(37, 99, 235, 0.12)',
    },
    typeBadgeAccent: {
      width: 10,
      height: 10,
      borderRadius: 999,
      flexShrink: 0,
      background: 'linear-gradient(135deg, #2563eb 0%, #38bdf8 100%)',
      boxShadow: '0 0 0 4px rgba(191, 219, 254, 0.75)',
    },
    typeBadgeContent: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    typeBadgeLabel: {
      color: '#64748b',
      fontSize: '0.62rem',
      fontWeight: 800,
      letterSpacing: '0.12em',
      lineHeight: 1.2,
      textTransform: 'uppercase',
    },
    typeBadgeValue: {
      color: '#0f172a',
      fontSize: '0.86rem',
      fontWeight: 800,
      lineHeight: 1.35,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    deleteButton: {
      marginTop: theme.spacing(1),
      color: '#dc2626',
      backgroundColor: 'rgba(254, 226, 226, 0.8)',
      '&:hover': {
        backgroundColor: 'rgba(254, 202, 202, 0.95)',
      },
    },
    actions: {
      padding: theme.spacing(2, 3, 2),
      justifyContent: 'space-between',
      alignItems: 'center',
      background:
        'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(248,250,252,0.96) 40%)',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: theme.spacing(1),
      },
    },
    actionText: {
      color: '#64748b',
      fontSize: '0.88rem',
      lineHeight: 1.6,
      maxWidth: 460,
    },
    actionShell: {
      display: 'flex',
      gap: theme.spacing(1),
      padding: theme.spacing(0.75),
      borderRadius: 999,
      border: '1px solid rgba(148, 163, 184, 0.18)',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.94) 0%, rgba(248,250,252,0.88) 100%)',
      boxShadow: '0 16px 36px rgba(15, 23, 42, 0.08)',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    secondaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      boxShadow: 'none',
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
    primaryButton: {
      minHeight: 44,
      padding: theme.spacing(0, 2.5),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 55%, #60a5fa 100%)',
      boxShadow: '0 14px 30px rgba(37, 99, 235, 0.24)',
      '&:hover': {
        background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)',
        boxShadow: '0 16px 34px rgba(37, 99, 235, 0.28)',
      },
      '&.Mui-disabled': {
        color: 'rgba(255,255,255,0.72)',
        background: 'linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)',
      },
      [theme.breakpoints.down('sm')]: {
        flex: 1,
      },
    },
    table: {
      marginTop: 0,
    },
  }),
);

const SubtaskListDetailDialogSchema = yup.object().shape({
  subtasks: yup.array().of(
    yup.object().shape({
      subject: yup.string().required('Subject is required'),
      smSubtaskTypeId: yup.number().required('Subtask type is required'),
      smSubtaskAssignees: yup
        .array()
        .of(
          yup.object().shape({
            userId: yup.number(),
          }),
        )
        .required('Assignee is required'),
      dueDate: yup
        .string()
        .nullable(true)
        .test(
          'dueDateFormat',
          'Invalid date format',
          (value) => !isNaN(new Date(value).getTime()),
        )
        .required('Due date is required'),
    }),
  ),
});

function defaultSubjectSubtaskPrefix(subtaskType: ISubtaskType): string {
  return `[${subtaskType.name || 'Subtask'}] `;
}

type TypeBadgeTheme = {
  background: string;
  borderColor: string;
  boxShadow: string;
  accent: string;
  accentRing: string;
  valueColor: string;
};

const typeBadgeThemes: TypeBadgeTheme[] = [
  {
    background:
      'linear-gradient(135deg, rgba(241,245,249,0.98) 0%, rgba(248,250,252,0.98) 100%)',
    borderColor: 'rgba(148, 163, 184, 0.22)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
    accent: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)',
    accentRing: 'rgba(226, 232, 240, 0.9)',
    valueColor: '#1e293b',
  },
  {
    background:
      'linear-gradient(135deg, rgba(244,247,246,0.98) 0%, rgba(250,252,251,0.98) 100%)',
    borderColor: 'rgba(148, 163, 184, 0.2)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
    accent: 'linear-gradient(135deg, #6b7280 0%, #9ca3af 100%)',
    accentRing: 'rgba(229, 231, 235, 0.9)',
    valueColor: '#334155',
  },
  {
    background:
      'linear-gradient(135deg, rgba(247,245,242,0.98) 0%, rgba(250,248,245,0.98) 100%)',
    borderColor: 'rgba(148, 163, 184, 0.2)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
    accent: 'linear-gradient(135deg, #78716c 0%, #a8a29e 100%)',
    accentRing: 'rgba(231, 229, 228, 0.92)',
    valueColor: '#292524',
  },
  {
    background:
      'linear-gradient(135deg, rgba(245,245,248,0.98) 0%, rgba(249,250,251,0.98) 100%)',
    borderColor: 'rgba(148, 163, 184, 0.22)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
    accent: 'linear-gradient(135deg, #6d7280 0%, #a1a1aa 100%)',
    accentRing: 'rgba(228, 228, 231, 0.92)',
    valueColor: '#27272a',
  },
  {
    background:
      'linear-gradient(135deg, rgba(243,247,248,0.98) 0%, rgba(248,250,252,0.98) 100%)',
    borderColor: 'rgba(148, 163, 184, 0.2)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
    accent: 'linear-gradient(135deg, #64748b 0%, #cbd5e1 100%)',
    accentRing: 'rgba(226, 232, 240, 0.92)',
    valueColor: '#334155',
  },
  {
    background:
      'linear-gradient(135deg, rgba(248,244,244,0.98) 0%, rgba(250,248,248,0.98) 100%)',
    borderColor: 'rgba(148, 163, 184, 0.2)',
    boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
    accent: 'linear-gradient(135deg, #737373 0%, #a3a3a3 100%)',
    accentRing: 'rgba(228, 228, 231, 0.9)',
    valueColor: '#3f3f46',
  },
];

function getTypeBadgeTheme(subtaskType?: ISubtaskType): TypeBadgeTheme {
  if (!subtaskType) {
    return {
      background:
        'linear-gradient(135deg, rgba(241,245,249,0.96) 0%, rgba(226,232,240,0.98) 100%)',
      borderColor: 'rgba(148, 163, 184, 0.24)',
      boxShadow: '0 8px 18px rgba(15, 23, 42, 0.05)',
      accent: 'linear-gradient(135deg, #94a3b8 0%, #cbd5e1 100%)',
      accentRing: 'rgba(226, 232, 240, 0.9)',
      valueColor: '#334155',
    };
  }

  const fallbackSeed = (subtaskType.name || '').split('').reduce((sum, char) => {
    return sum + char.charCodeAt(0);
  }, 0);
  const paletteIndex = Math.abs(
    (subtaskType.id || fallbackSeed) % typeBadgeThemes.length,
  );
  return typeBadgeThemes[paletteIndex];
}

const SubtaskListDetailDialog: React.FC<SubtaskListDetailDialogProps> = (props) => {
  const classes = useStyles();
  const { nextClick, backClick, closeClick, subtaskTypeList } = props;
  const dispatch = useDispatch();
  const { createTaskPayload, selectedSubtaskType, createSubtasksPayload } = useSelector<
    AppState,
    TaskCreateState
  >((state) => state.taskCreate);

  const { userId } = useSelector<AppState, AuthState>((state) => state.auth);

  const handleSubmit = (values: any) => {
    const createSubtasks = values.subtasks.map((subtask: any) => {
      return { ...subtask, requesterId: userId };
    });
    dispatch(createSubtasksInTaskCreate(createSubtasks));
    nextClick();
  };

  const getInitialValue = useMemo(
    () => () =>
      selectedSubtaskType.flatMap((subtaskTypeName) => {
        const { subject, dueDate } = createTaskPayload;
        const subtaskType = subtaskTypeList.find((st) => st.name === subtaskTypeName);
        if (!subtaskType || !subtaskType.id) {
          return [];
        }
        const defaultSubtaskSubject = `${defaultSubjectSubtaskPrefix(
          subtaskType,
        )}${subject}`;
        const createdSubtask = createSubtasksPayload.find(
          (st) =>
            st.subject === defaultSubtaskSubject && st.smSubtaskTypeId === subtaskType.id,
        );

        return [
          {
            subject: `${defaultSubjectSubtaskPrefix(subtaskType)}${subject}`,
            smSubtaskTypeId: subtaskType.id,
            assignee: createdSubtask ? createdSubtask.assignee : null,
            smSubtaskAssignees: createdSubtask ? createdSubtask.smSubtaskAssignees : null,
            dueDate,
          },
        ];
      }),
    [subtaskTypeList, createTaskPayload, createSubtasksPayload, selectedSubtaskType],
  );

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{ subtasks: getInitialValue() }}
        validationSchema={SubtaskListDetailDialogSchema}
        onSubmit={handleSubmit}
        validateOnChange={true}
      >
        {({ values, errors, setFieldValue }) => {
          const disabledNextButton = values.subtasks.length === 0;
          return (
            <Form>
              <DialogTitle className={classes.dialogTitle} disableTypography>
                <Box className={classes.titleContent}>
                  <Box className={classes.titleText}>
                    <Typography variant="h4" className={classes.titleHeading}>
                      Create Subtasks
                    </Typography>
                    <Typography variant="body1" className={classes.titleSubtitle}>
                      Refine each subtask before creation.
                    </Typography>
                  </Box>
                  <Box className={classes.titleActions}>
                    <IconButton
                      aria-label="close"
                      onClick={() => {
                        closeClick();
                      }}
                      className={classes.closeButton}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent className={classes.content}>
                <Box className={classes.tableMeta}>
                  <Box>
                    <Typography variant="subtitle1" className={classes.metaTitle}>
                      Subtask details
                    </Typography>
                    <Typography variant="body2" className={classes.metaSubtitle}>
                      Complete each row before moving forward. You can still remove rows
                      that are no longer needed.
                    </Typography>
                  </Box>
                  <Box className={classes.countBadge}>
                    {values.subtasks.length} subtasks prepared
                  </Box>
                </Box>
                <Box className={classes.tableSurface}>
                  <Box className={classes.tableScrollArea}>
                    <Table className={classes.table} size="small">
                      <TableHead>
                        <TableRow className={classes.tableHeadRow}>
                          <TableCell className={classes.tableHeadCell}>Subject</TableCell>
                          <TableCell className={classes.tableHeadCell}>Type</TableCell>
                          <TableCell className={classes.tableHeadCell}>
                            Assignee
                          </TableCell>
                          <TableCell className={classes.tableHeadCell}>
                            Due date
                          </TableCell>
                          <TableCell className={classes.tableHeadCell}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <FieldArray
                          name="subtasks"
                          render={(arrayHelpers) => (
                            <>
                              {values.subtasks.map((value, index) => {
                                const getErrorMessage = (
                                  fieldName: string,
                                  rowIndex: number,
                                ) => getIn(errors, `subtasks[${rowIndex}].${fieldName}`);
                                const subtaskType = subtaskTypeList.find(
                                  (s) => s.id === value.smSubtaskTypeId,
                                );
                                const typeBadgeTheme = getTypeBadgeTheme(subtaskType);
                                return (
                                  <TableRow key={index} className={classes.resultRow}>
                                    <TableCell className={classes.subjectCell}>
                                      <TextField
                                        value={value.subject}
                                        margin="dense"
                                        variant="outlined"
                                        onChange={(event) => {
                                          const newSubject = event.target.value;
                                          arrayHelpers.replace(index, {
                                            ...values.subtasks[index],
                                            subject: newSubject,
                                          });
                                        }}
                                        fullWidth
                                        error={!!getErrorMessage('subject', index)}
                                        helperText={getErrorMessage('subject', index)}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.typeCell}>
                                      <Box
                                        className={classes.typeBadge}
                                        style={{
                                          background: typeBadgeTheme.background,
                                          borderColor: typeBadgeTheme.borderColor,
                                          boxShadow: typeBadgeTheme.boxShadow,
                                        }}
                                      >
                                        <Box
                                          className={classes.typeBadgeAccent}
                                          style={{
                                            background: typeBadgeTheme.accent,
                                            boxShadow: `0 0 0 4px ${typeBadgeTheme.accentRing}`,
                                          }}
                                        />
                                        <Box className={classes.typeBadgeContent}>
                                          <Typography
                                            className={classes.typeBadgeValue}
                                            style={{ color: typeBadgeTheme.valueColor }}
                                          >
                                            {subtaskType ? subtaskType.name : '-'}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.assigneeCell}>
                                      <UserComboBox
                                        handleChange={(users: User[]) => {
                                          arrayHelpers.replace(index, {
                                            ...values.subtasks[index],
                                            assignee: users ? users : null,
                                            smSubtaskAssignees: users
                                              ? users.map((user) => ({ userId: user.id }))
                                              : [],
                                          });
                                        }}
                                        selectedValue={
                                          (value.assignee as unknown) as User[]
                                        }
                                        error={
                                          !!getErrorMessage('smSubtaskAssignees', index)
                                        }
                                        // helperText={getErrorMessage(
                                        //   'smSubtaskAssignees',
                                        //   index,
                                        // )}
                                        variant="outlined"
                                        isMulti
                                      />
                                    </TableCell>
                                    <TableCell className={classes.dueDateCell}>
                                      <KeyboardDatePicker
                                        margin="dense"
                                        format="yyyy-MM-dd"
                                        value={value.dueDate}
                                        onChange={(selectedDate, selectedStringDate) => {
                                          setFieldValue(
                                            `subtasks[${index}].dueDate` as 'subtasks',
                                            selectedStringDate,
                                          );
                                        }}
                                        inputVariant="outlined"
                                        error={!!getErrorMessage('dueDate', index)}
                                        helperText={getErrorMessage('dueDate', index)}
                                      />
                                    </TableCell>
                                    <TableCell className={classes.actionCell}>
                                      <Tooltip title="Delete">
                                        <span>
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              arrayHelpers.remove(index);
                                            }}
                                            className={classes.deleteButton}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </>
                          )}
                        />
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </DialogContent>
              <DialogActions className={classes.actions}>
                <Typography className={classes.actionText}>
                  Review every row before continuing so the generated subtasks already
                  have the right owner and due date.
                </Typography>
                <Box>
                  <Button
                    onClick={backClick}
                    startIcon={<PreviousIcon />}
                    variant="outlined"
                    color="primary"
                    className={classes.secondaryButton}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<NextIcon />}
                    disabled={disabledNextButton}
                    className={classes.primaryButton}
                  >
                    Next
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default SubtaskListDetailDialog;
